import { useEffect, useState } from 'react';

/** Set once the user has seen the badge pop in, so the flourish plays exactly
 *  once — at the moment they become Pro — not on every later page load. */
const SEEN_KEY = 'uzt.proBadgeSeen';

/**
 * A small gold star at the corner of a Pro subscriber's avatar — a quiet
 * "this account is Pro" mark, and the first thing a buyer sees flip on the
 * instant a purchase lands (the session refreshes to Pro and this appears).
 *
 * Purely presentational: the caller decides whether to render it (gate on
 * `isPaidUser`). Absolutely positioned, so the avatar element it sits inside
 * must be `position: relative`. The star is an inline <svg> ON TOP of the gold
 * disc (not a background layer — a background image would sit *under* a second
 * background layer), with a surface-coloured ring so it reads against the
 * avatar behind it. `size` is the badge diameter in px; pass ~38–42% of the
 * avatar's size (≈15 on the 40px topbar avatar, ≈12 on the 28px sidebar one).
 *
 * The pop-in flourish plays only the FIRST time it appears (localStorage flag),
 * so it celebrates becoming Pro without re-firing on every navigation — and it
 * is `motion-safe`, so reduced-motion users just get the static badge.
 */
export function ProBadge({ size = 15 }: { size?: number }) {
  const [pop, setPop] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem(SEEN_KEY)) {
        setPop(true);
        localStorage.setItem(SEEN_KEY, '1');
      }
    } catch {
      /* private mode / storage disabled — skip the one-time flourish */
    }
  }, []);

  return (
    <span
      aria-label="Pro"
      title="Pro"
      className={`pointer-events-none absolute -bottom-0.5 -right-0.5 grid place-items-center rounded-full border-2 border-bone-surface bg-[#D4A53A] shadow-[0_1px_2px_rgba(0,0,0,0.28)]${
        pop ? ' motion-safe:animate-badge-pop' : ''
      }`}
      style={{ width: size, height: size }}
    >
      <svg
        viewBox="0 0 24 24"
        fill="#fff"
        aria-hidden="true"
        style={{ width: size * 0.6, height: size * 0.6 }}
      >
        <path d="M12 2.6l2.55 5.6 6.15.62-4.6 4.12 1.32 6.04L12 15.9l-5.42 3.08 1.32-6.04-4.6-4.12 6.15-.62z" />
      </svg>
    </span>
  );
}
